import type { PaginationWindowEntry, PaginationWindowOptions } from "./Pagination.types";

/**
 * Builds the sequence of 1-based page numbers and `"ellipsis"` markers.
 *
 * This is **general pagination UI logic**: same pattern used across the web — always show
 * the first and last page when collapsed, a bounded window around the current page, and
 * ellipses for skipped ranges (see e.g. accessible pagination patterns and shadcn-style bars).
 *
 * - Works for any total page count (`totalPages` from API length / page size).
 * - **1-based** indices throughout (typical for user-facing labels and URLs).
 * - Clamps `currentPage` to `[1, totalPages]`; empty result when `totalPages < 1`.
 */
export function getPaginationWindow(
  currentPage: number,
  totalPages: number,
  options?: PaginationWindowOptions
): PaginationWindowEntry[] {
  const collapseThreshold = options?.collapseThreshold ?? 7;

  if (totalPages < 1) {
    return [];
  }

  const safeCurrent = Math.min(Math.max(1, currentPage), totalPages);

  if (totalPages <= collapseThreshold) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: PaginationWindowEntry[] = [1];

  if (safeCurrent <= 4) {
    for (let i = 2; i <= 5; i += 1) {
      pages.push(i);
    }
    pages.push("ellipsis");
    pages.push(totalPages);
  } else if (safeCurrent >= totalPages - 3) {
    pages.push("ellipsis");
    for (let i = totalPages - 4; i <= totalPages; i += 1) {
      pages.push(i);
    }
  } else {
    pages.push("ellipsis");
    pages.push(safeCurrent - 1);
    pages.push(safeCurrent);
    pages.push(safeCurrent + 1);
    pages.push("ellipsis");
    pages.push(totalPages);
  }

  return pages;
}
